import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Video, Share2, Palette, Globe, Smartphone, Bot, ArrowUpRight } from 'lucide-react';

const services = [
  {
    icon: Video,
    title: 'Producción de Video',
    description: 'Filmación profesional y edición de video para marcas y contenido de marketing personal.',
    features: ['Videos Corporativos', 'Contenido Social', 'Demos de Producto', 'Brand Stories'],
    span: 'md:col-span-2',
    link: 'https://www.youtube.com',
  },
  {
    icon: Share2,
    title: 'Gestión de Redes Sociales',
    description: 'Gestión estratégica de cuentas de redes sociales, creación de contenido y optimización de engagement.',
    features: ['Estrategia de Contenido', 'Community Management', 'Analytics & Reportes', 'Campañas Pagadas'],
    span: '',
    link: 'https://www.instagram.com',
  },
  {
    icon: Palette,
    title: 'Identidad de Marca & Logo',
    description: 'Creación de logos profesionales e identidades de marca que establecen una presencia visual fuerte.',
    features: ['Diseño de Logo', 'Manual de Marca', 'Identidad Visual', 'Estrategia de Marca'],
    span: '',
    link: 'https://www.behance.net',
  },
  {
    icon: Globe,
    title: 'Desarrollo Web',
    description: 'Diseño y desarrollo de sitios web profesionales optimizados para visibilidad y conversión.',
    features: ['Desarrollo Custom', 'E-commerce', 'Optimización SEO', 'Performance'],
    span: 'md:col-span-2',
    link: 'https://www.awwwards.com',
  },
  {
    icon: Smartphone,
    title: 'Desarrollo de Apps',
    description: 'Desarrollo de aplicaciones móviles y web adaptadas a las necesidades del negocio.',
    features: ['iOS & Android', 'Web Apps', 'Diseño UI/UX', 'Integración API'],
    span: '',
    link: 'https://www.apple.com/app-store',
  },
  {
    icon: Bot,
    title: 'Automatización con IA',
    description: 'Implementación de herramientas de IA como soporte automatizado, sistemas de marketing y workflows inteligentes.',
    features: ['Chatbots', 'Automatización de Flujos', 'Análisis de Datos', 'Marketing AI'],
    span: '',
    link: 'https://openai.com',
  },
];

export default function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="services"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#0a0a0a]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-xs font-mono tracking-[0.3em] uppercase text-[#D4AF37] mb-4 block">
            What We Do
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
            Nuestros Servicios
          </h2>
        </motion.div>

        {/* Services Bento Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.a
              key={service.title}
              href={service.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
              className={`group relative p-8 md:p-10 bg-zinc-950/80 border border-white/5 hover:border-[#D4AF37]/50 transition-all duration-500 overflow-hidden ${service.span} cursor-pointer`}
              data-testid={`service-${index}`}
            >
              {/* Hover gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative z-10">
                {/* Icon and Arrow */}
                <div className="flex items-start justify-between mb-6">
                  <service.icon
                    size={36}
                    className="text-zinc-500 group-hover:text-[#D4AF37] transition-colors duration-300"
                  />
                  <ArrowUpRight
                    size={24}
                    className="text-zinc-700 group-hover:text-[#D4AF37] transition-all duration-300 transform group-hover:-translate-y-1 group-hover:translate-x-1"
                  />
                </div>

                {/* Title */}
                <h3 className="text-xl md:text-2xl font-semibold text-white group-hover:text-[#D4AF37] transition-colors duration-300 mb-4">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-sm md:text-base text-zinc-400 leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-2">
                  {service.features.map((feature) => (
                    <span
                      key={feature}
                      className="text-xs tracking-wider uppercase px-3 py-1 bg-white/5 text-zinc-500 group-hover:bg-[#D4AF37]/10 group-hover:text-[#D4AF37] transition-all duration-300"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
